import {
  createDefaultSection,
  createDefaultContainer,
  createDefaultWidget,
  generateId,
  DEFAULT_HEADER_CONFIG,
  DEFAULT_FOOTER_CONFIG,
} from './pageDocumentModel';

// Default fallback datasets for bundled ERP pages (zero-loss hydration)
const DEFAULT_PARTNER_FEATURES = [
  {
    icon: '/svgs/becomePartner/warranty.svg',
    title: '12-Month Warranty for Peace of Mind',
    description: 'Devices sold through Zobiz come with up to 12-month warranty, ensuring reliable, high-quality products.',
  },
  {
    icon: '/svgs/becomePartner/functionalDevice.svg',
    title: '100% Functional Devices',
    description: 'Every device listed is fully tested and guaranteed to be 100% functional with zero hardware defects.',
  },
  {
    icon: '/svgs/becomePartner/latestModel.svg',
    title: 'Access to the Latest Models',
    description: 'Stay ahead of competition with high-demand flagship and mid-range devices from trusted brands.',
  },
  {
    icon: '/svgs/becomePartner/free-delivery_6912177 1.svg',
    title: 'Free Doorstep Delivery',
    description: 'Enjoy free, insured doorstep delivery across India on every wholesale and partner order.',
  },
  {
    icon: '/svgs/becomePartner/multiPayment.svg',
    title: 'Multiple Payment Options',
    description: 'Flexible payment terms including UPI, netbanking, credit cards, EMI, and COD for verified partners.',
  },
  {
    icon: '/svgs/becomePartner/wholeSale.svg',
    title: 'Hassle-Free Wholesale Ordering',
    description: 'Browse live catalog, view real-time grading and stock counts, and place orders directly on the app.',
  },
  {
    icon: '/svgs/becomePartner/b2b.svg',
    title: 'Competitive B2B Margins',
    description: 'Maximize your retail profit margins with transparent volume discounts and tier pricing.',
  },
  {
    icon: '/svgs/becomePartner/realTimeInvent.svg',
    title: 'Real-Time Inventory & Order Tracking',
    description: 'Track consignments, manage order status, and reserve fast-moving stock with live inventory visibility.',
  },
  {
    icon: '/svgs/becomePartner/ecoFriendly.svg',
    title: 'Eco-Friendly Circular Tech',
    description: 'Contribute to sustainable e-waste reduction by circulating certified pre-owned smartphones and laptops.',
  },
];

const DEFAULT_PARTNER_STEPS = [
  {
    step: '1',
    img: '/svgs/becomePartner/step1.png',
    title: 'Download App',
    desc: 'Download the Zobiz Retailer app from Google Play Store or Apple App Store.',
  },
  {
    step: '2',
    img: '/svgs/becomePartner/step2.png',
    title: 'Register Yourself',
    desc: 'Upload business proof (GST certificate or Udyog Aadhaar), PAN, and Aadhaar card for instant verification.',
  },
  {
    step: '3',
    img: '/svgs/becomePartner/step3.png',
    title: 'Grow Your Business',
    desc: 'Browse certified stock, place wholesale orders at trade discounts, and enjoy doorstep delivery.',
  },
];

const DEFAULT_WHOLESALER_STEPS = [
  {
    step: '1',
    img: '/assets/app.svg',
    title: 'Install Wholesale App',
    desc: 'Access verified bulk device inventory anytime on your mobile device.',
  },
  {
    step: '2',
    img: '/assets/register.svg',
    title: 'Complete KYC & Verification',
    desc: 'Submit trade registration details to unlock wholesale price tiers.',
  },
  {
    step: '3',
    img: '/assets/grow.svg',
    title: 'Order Graded Bulk Lots',
    desc: 'Receive securely packaged, fully inspected lots with comprehensive warranty coverage.',
  },
];

const DEFAULT_REACH_US_BOXES = [
  {
    icon: '🎧',
    title: 'Connect with Us',
    text: 'For customer support, order assistance, or trade queries, reach out directly via call or email.',
  },
  {
    icon: '🏢',
    title: 'Corporate Office',
    text: 'Zobox Protech Limited | E218C, Sector - 63, Noida, Uttar Pradesh - 201301, India',
  },
  {
    icon: '📍',
    title: 'Head Office',
    text: 'Zobox Protech Limited | 4th Floor, 6/77, WEA, Karol Bagh, New Delhi - 110005, India',
  },
];

const DEFAULT_GRADE_TABS = [
  {
    name: 'Excellent',
    points: ['100% Fully Functional', '12 Month Warranty Coverage', 'Original OEM Display', 'Zero Hardware Defects'],
    displayText: 'Flawless display, no dead pixels, perfect touch response',
    physicalText: 'Like new condition, pristine casing with no noticeable scratches',
  },
  {
    name: 'Superb',
    points: ['100% Fully Functional', '12 Month Warranty Coverage', 'Fully Tested Screen', 'Zero Hardware Defects'],
    displayText: 'Clear display with full touch accuracy; minor hairline scratch possible',
    physicalText: 'Very minor cosmetic signs of gentle use, fully intact structure',
  },
  {
    name: 'Good',
    points: ['100% Fully Functional', '6 Month Warranty Coverage', 'Tested Responsive Screen', 'Best Value Price'],
    displayText: 'Bright functional screen with possible light cosmetic scratches',
    physicalText: 'Visible cosmetic wear or minor discoloration on body, outstanding value',
  },
];

function parseJsonSafe(val, fallback) {
  if (!val) return fallback;
  if (Array.isArray(val) || typeof val === 'object') return val;
  try {
    const parsed = JSON.parse(val);
    return parsed || fallback;
  } catch (e) {
    return fallback;
  }
}

/**
 * Converts a legacy CMS page record (with optional page blocks) into the canonical
 * Page -> Section -> Container -> Widget document model in deterministic sequential order.
 */
export function legacyToCanonical(pageRecord = {}, pageBlocks = []) {
  // Capture all original fields to guarantee zero data loss
  const customFields = {
    ...pageRecord,
    ...(pageRecord.customFields || {}),
  };

  // If the record already has valid canonical sections, reuse them while preserving customFields
  if (Array.isArray(pageRecord.sections) && pageRecord.sections.length > 0) {
    return {
      version: pageRecord.version || 1,
      id: pageRecord.id || generateId('page'),
      title: pageRecord.title || 'Untitled Page',
      slug: pageRecord.slug || '',
      route: pageRecord.route || (pageRecord.slug ? `/content/${pageRecord.slug}` : ''),
      layout: pageRecord.layout || 'custom',
      status: pageRecord.status || 'draft',
      navbar_label: pageRecord.navbar_label || '',
      is_active: pageRecord.is_active !== false,
      display_order: Number(pageRecord.display_order || 0),
      seo: {
        title: pageRecord.seo_title || pageRecord.seo?.title || '',
        description: pageRecord.seo_description || pageRecord.seo?.description || '',
        keywords: pageRecord.seo_keywords || pageRecord.seo?.keywords || '',
        og_image: pageRecord.og_image || pageRecord.seo?.og_image || '',
        canonical_url: pageRecord.canonical_url || pageRecord.seo?.canonical_url || '',
      },
      header: pageRecord.header || JSON.parse(JSON.stringify(DEFAULT_HEADER_CONFIG)),
      footer: pageRecord.footer || JSON.parse(JSON.stringify(DEFAULT_FOOTER_CONFIG)),
      sections: pageRecord.sections,
      customFields,
    };
  }

  const sections = [];
  const layout = pageRecord.layout || 'custom';
  const slug = String(pageRecord.slug || '').toLowerCase().trim();

  // ============================================================
  // SEQUENCE 1: Hero Header / Top Banner Section
  // ============================================================
  const heroImage =
    pageRecord.banner_image ||
    pageRecord.featured_image ||
    pageRecord.image ||
    (slug === 'become-partner' ? '/svgs/becomePartner/becomeAPartner.svg' : '') ||
    (slug === 'zobiz-partner' ? '/banners/BusinessBanner.png' : '') ||
    (slug === 'wholesaler' ? '/assets/wholesale_banner.svg' : '') ||
    (slug === 'contact-us' ? '/assets/contact-us-banner.png' : '');

  const heroEyebrow = pageRecord.eyebrow || (
    slug === 'become-partner' ? 'BECOME A PARTNER' :
    slug === 'zobiz-partner' ? 'ZOBIZ RETAILER APP' :
    slug === 'wholesaler' ? 'WHOLESALE' :
    slug === 'contact-us' ? 'LET’S TALK TECH, TRUST & TRANSFORMATION' :
    slug === 'grade-condition' ? 'QUALITY GRADES' :
    slug === 'refurbished' ? 'REFURBISHED DEVICES' :
    slug === 'about' ? 'ABOUT ZOBOX' :
    slug === 'warranty' ? 'WARRANTY' :
    slug === 'faq' ? 'HELP & FAQ' :
    'ZOBOX'
  );

  const heroTitle = pageRecord.title || pageRecord.heading || pageRecord.page_name || 'ZOBOX | Quality Refurbished Devices';
  const heroExcerpt = pageRecord.excerpt || pageRecord.description || (
    slug === 'become-partner' ? 'Download the Zobiz Retailer App and gain access to fully tested, functional refurbished devices with the latest models, free delivery, and a 12-month warranty.' :
    slug === 'zobiz-partner' ? 'A B2B platform offering high-quality refurbished devices at wholesale prices for mobile retailers.' :
    slug === 'wholesaler' ? 'Tested refurbished devices, reliable volume supply, and consistent device grading for wholesalers across India.' :
    slug === 'contact-us' ? 'Whether you’re buying, partnering, or just curious—ZOBOX is here to help.' :
    slug === 'grade-condition' ? 'Transparent definitions for Excellent, Superb, and Good grades across all ZOBOX certified devices.' :
    slug === 'refurbished' ? 'Discover how ZOBOX tests, grades, and certifies pre-owned electronics to ensure premium quality at accessible prices.' :
    ''
  );

  const heroSection = createDefaultSection('Hero Header');
  heroSection.style.backgroundColor = layout === 'gateway' ? '#1e2669' : '#ffffff';
  heroSection.style.paddingTop = '32px';
  heroSection.style.paddingBottom = '32px';

  const heroContainer = heroSection.children[0];
  heroContainer.children = [
    createDefaultWidget('page_hero', {
      name: 'Page Hero Banner',
      content: {
        eyebrow: heroEyebrow,
        title: heroTitle,
        excerpt: heroExcerpt,
        featured_image: heroImage,
        cta_text: pageRecord.cta_text || '',
        cta_url: pageRecord.cta_url || '',
        layout,
      },
      style: {
        textColor: layout === 'gateway' ? '#ffffff' : '#1e293b',
        eyebrowColor: layout === 'gateway' ? '#93c5fd' : '#2b3380',
        alignment: 'left',
      },
    }),
  ];
  sections.push(heroSection);

  // ============================================================
  // SEQUENCE 2: Main Rich Content / Policy Body (if present)
  // ============================================================
  const bodyContent = pageRecord.content || pageRecord.policy_content;
  if (bodyContent && String(bodyContent).trim()) {
    const contentSection = createDefaultSection('Main Content');
    const contentContainer = contentSection.children[0];
    contentContainer.children = [
      createDefaultWidget('text', {
        name: 'Page Body Text',
        content: { content: bodyContent },
        style: { fontSize: '16px', color: '#334155', lineHeight: '1.7' },
      }),
    ];
    sections.push(contentSection);
  }

  // ============================================================
  // SEQUENCE 3: About Page Specific Sections
  // ============================================================
  if (slug === 'about' || pageRecord.promise_title || pageRecord.leader_name) {
    if (pageRecord.promise_title || pageRecord.promise_eyebrow || slug === 'about') {
      const promiseSection = createDefaultSection('Our Promise');
      promiseSection.children[0].children = [
        createDefaultWidget('heading', {
          name: 'Promise Heading',
          content: { level: 'h2', text: pageRecord.promise_title || 'Resources should not be wasted.' },
        }),
        createDefaultWidget('text', {
          name: 'Promise Eyebrow',
          content: { content: pageRecord.promise_eyebrow || 'OUR PROMISE' },
        }),
      ];
      sections.push(promiseSection);
    }

    if (pageRecord.history_title || pageRecord.history_image || slug === 'about') {
      const historySection = createDefaultSection('History & Journey');
      historySection.children[0].children = [
        createDefaultWidget('heading', {
          name: 'History Heading',
          content: { level: 'h2', text: pageRecord.history_title || 'The ZOBOX journey' },
        }),
        createDefaultWidget('image', {
          name: 'History Graphic',
          content: { url: pageRecord.history_image || '/assets/abouHistory.svg', alt: 'ZOBOX History' },
        }),
      ];
      sections.push(historySection);
    }

    if (pageRecord.ecosystem_title || slug === 'about') {
      const ecoSection = createDefaultSection('Our Ecosystem');
      ecoSection.children[0].children = [
        createDefaultWidget('heading', {
          name: 'Ecosystem Heading',
          content: { level: 'h2', text: pageRecord.ecosystem_title || 'Connected by better technology' },
        }),
        createDefaultWidget('text', {
          name: 'Ecosystem Eyebrow',
          content: { content: pageRecord.ecosystem_eyebrow || 'OUR ECOSYSTEM' },
        }),
      ];
      sections.push(ecoSection);
    }

    if (pageRecord.leader_name || slug === 'about') {
      const leaderSection = createDefaultSection('Leadership');
      leaderSection.children[0].children = [
        createDefaultWidget('heading', {
          name: 'Leader Name',
          content: { level: 'h2', text: pageRecord.leader_name || 'Neeraj Chopra' },
        }),
        createDefaultWidget('text', {
          name: 'Leader Bio',
          content: {
            content: pageRecord.leader_bio ||
              "As the founder and CEO of Zobox Protech Limited, I'm driven by a passion for innovation and sustainability. With 20 years of experience in retail, refurbishment and cross-border trade, I've learned the importance of excellence, integrity and customer-centricity.",
          },
        }),
        createDefaultWidget('image', {
          name: 'Leader Photo',
          content: { url: pageRecord.leader_image || '/assets/NeerajChopra.svg', alt: 'Neeraj Chopra - CEO' },
        }),
      ];
      sections.push(leaderSection);
    }

    if (pageRecord.work_image || slug === 'about') {
      const workSection = createDefaultSection('Work With Us');
      workSection.children[0].children = [
        createDefaultWidget('image', {
          name: 'Work With Us Banner',
          content: { url: pageRecord.work_image || '/assets/WorkWithUs.svg', alt: 'Work With Us' },
        }),
      ];
      sections.push(workSection);
    }
  }

  // ============================================================
  // SEQUENCE 4: Step Process / Onboarding Steps Section
  // (become-partner, zobiz-partner, wholesaler, channel-partner)
  // ============================================================
  const isPartnerPage = ['become-partner', 'zobiz-partner', 'channel-partner', 'partner'].includes(slug);
  const isWholesalePage = slug === 'wholesaler';

  if (isPartnerPage || isWholesalePage || pageRecord.partner_steps) {
    const rawSteps = parseJsonSafe(
      pageRecord.partner_steps,
      isWholesalePage ? DEFAULT_WHOLESALER_STEPS : DEFAULT_PARTNER_STEPS
    );
    const stepSection = createDefaultSection('How to Partner (Step Process)');
    stepSection.children[0].children = [
      createDefaultWidget('step_process', {
        name: 'Onboarding Steps',
        content: {
          title: pageRecord.partner_title || 'How to Partner with Zobiz',
          steps: rawSteps,
        },
      }),
    ];
    sections.push(stepSection);
  }

  // ============================================================
  // SEQUENCE 5: Feature Cards Grid Section (9 Key Benefits)
  // ============================================================
  if (isPartnerPage || pageRecord.features_json) {
    const rawFeatures = parseJsonSafe(pageRecord.features_json, DEFAULT_PARTNER_FEATURES);
    const featureSection = createDefaultSection('Why Choose Us (Features Grid)');
    featureSection.children[0].children = [
      createDefaultWidget('feature_grid', {
        name: '9 Key Partner Features',
        content: {
          title: pageRecord.features_heading || 'Why Choose Zobiz Retailer App?',
          features: rawFeatures,
        },
      }),
    ];
    sections.push(featureSection);
  }

  // ============================================================
  // SEQUENCE 6: Infographic / Media Banners
  // ============================================================
  if (slug === 'zobiz-partner' || pageRecord.why_zobix_image) {
    const infoSection = createDefaultSection('Why Zobox Infographic');
    infoSection.children[0].children = [
      createDefaultWidget('media_banner', {
        name: 'Why Zobox Banner',
        content: {
          image: pageRecord.why_zobix_image || '/banners/whyZobix.png',
          alt: 'Why ZOBOX?',
          title: 'Why ZOBOX Retailer Proposition',
        },
      }),
    ];
    sections.push(infoSection);
  }

  if (slug === 'wholesaler' || pageRecord.infographic_image) {
    const infoSection = createDefaultSection('Wholesale Proposition');
    infoSection.children[0].children = [
      createDefaultWidget('media_banner', {
        name: 'Wholesale Process Infographic',
        content: {
          image: pageRecord.infographic_image || '/assets/Zobiz.svg',
          alt: 'ZOBOX Wholesaler Ecosystem',
          title: 'Wholesaler Ordering Process',
        },
      }),
    ];
    sections.push(infoSection);
  }

  if (slug === 'zobiz-partner' || slug === 'become-partner') {
    const appBannerSection = createDefaultSection('Application Download Banner');
    appBannerSection.children[0].children = [
      createDefaultWidget('media_banner', {
        name: 'App Promo Banner',
        content: {
          image: pageRecord.app_banner || '/zobixPartner/applicationBannerZobox.png',
          alt: 'Zobiz Retailer Application',
          title: 'Download Zobiz Retailer App Today',
        },
      }),
    ];
    sections.push(appBannerSection);
  }

  if (slug === 'wholesaler' || pageRecord.success_banner) {
    const successSection = createDefaultSection('Wholesale Success Story');
    successSection.children[0].children = [
      createDefaultWidget('media_banner', {
        name: 'Success Banner',
        content: {
          image: pageRecord.success_banner || '/assets/success.svg',
          alt: 'Wholesale Success Story',
          title: 'Join Our Successful Network',
        },
      }),
    ];
    sections.push(successSection);
  }

  // ============================================================
  // SEQUENCE 7: Contact Channels & Reach Us (for contact pages)
  // ============================================================
  if (slug === 'contact-us' || slug === 'contact') {
    const contactSection = createDefaultSection('Direct Contact Channels');
    contactSection.children[0].children = [
      createDefaultWidget('contact_channels', {
        name: 'WhatsApp, Call & Email Channels',
        content: {
          whatsapp: pageRecord.whatsapp_url || 'https://wa.me/919873788964',
          phone: pageRecord.phone || '9873788964',
          email: pageRecord.email || 'support@zobox.in',
        },
      }),
    ];
    sections.push(contactSection);

    const reachSection = createDefaultSection('Reach Us (Office & Facility Locations)');
    reachSection.children[0].children = [
      createDefaultWidget('reach_us_boxes', {
        name: 'Reach Us Corporate Boxes',
        content: {
          title: 'Reach Us',
          boxes: parseJsonSafe(pageRecord.reach_us, DEFAULT_REACH_US_BOXES),
        },
      }),
    ];
    sections.push(reachSection);
  }

  // ============================================================
  // SEQUENCE 8: Grade & Condition Tabs (for GradeCondition page)
  // ============================================================
  if (slug === 'grade-condition' || pageRecord.grade_tabs) {
    const gradeSection = createDefaultSection('Grading Standards & Criteria');
    gradeSection.children[0].children = [
      createDefaultWidget('grade_tabs', {
        name: 'Condition Grading Tabs',
        content: {
          title: pageRecord.grade_title || 'Our Grades Explanations',
          tabs: parseJsonSafe(pageRecord.grade_tabs, DEFAULT_GRADE_TABS),
        },
      }),
    ];
    sections.push(gradeSection);
  }

  // ============================================================
  // SEQUENCE 9: Refurbished Video Section (for Refurbished page)
  // ============================================================
  if (slug === 'refurbished' || pageRecord.video_url) {
    const videoSection = createDefaultSection('Refurbishment Process Video');
    videoSection.children[0].children = [
      createDefaultWidget('video_player', {
        name: '32-Point Quality Inspection Video',
        content: {
          url: pageRecord.video_url || 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          title: 'Our 32-Point Quality Inspection Process',
          poster: pageRecord.video_poster || '/assets/buy-phone.png',
        },
      }),
    ];
    sections.push(videoSection);
  }

  // ============================================================
  // SEQUENCE 10: FAQ Accordion Section
  // ============================================================
  if (slug === 'faq' || pageRecord.faqs) {
    const faqSection = createDefaultSection('Frequently Asked Questions');
    faqSection.children[0].children = [
      createDefaultWidget('faq_accordion', {
        name: 'FAQ Accordion',
        content: {
          title: pageRecord.faq_title || 'Frequently Asked Questions',
          eyebrow: 'HELP & SUPPORT',
          items: parseJsonSafe(pageRecord.faqs, [
            { q: 'How do I get started with Zobiz Application?', a: 'Download the application from Play Store or App Store, complete simple KYC, and start placing orders.' },
            { q: 'What is the warranty period for refurbished phones?', a: 'Every ZOBOX certified device comes with 6 to 12 months comprehensive warranty support.' },
            { q: 'Is Cash on Delivery available?', a: 'Yes, COD is available for eligible retail partners across India.' },
          ]),
        },
      }),
    ];
    sections.push(faqSection);
  }

  // ============================================================
  // SEQUENCE 11: Dynamic Page Items / Blocks Section (from page_items or pageBlocks)
  // ============================================================
  const items = Array.isArray(pageRecord.page_items) && pageRecord.page_items.length > 0
    ? pageRecord.page_items
    : pageBlocks;

  if (Array.isArray(items) && items.length > 0) {
    const cardsSection = createDefaultSection('Custom Page Items');
    cardsSection.style.backgroundColor = '#f8fafc';

    const cardsContainer = createDefaultContainer('2-column', 'Blocks Grid');
    cardsContainer.style.maxWidth = '1200px';

    items.forEach((item, idx) => {
      const bType = item.block_type || 'card';
      let widgetType = 'card';
      let widgetContent = {};

      if (bType === 'image') {
        widgetType = 'image';
        widgetContent = {
          url: item.image || '',
          alt: item.alt || '',
          caption: item.title || '',
        };
      } else if (bType === 'action') {
        widgetType = 'action_button';
        widgetContent = {
          label: item.cta_label || item.title || 'Learn more',
          url: item.href || '',
        };
      } else if (bType === 'note') {
        widgetType = 'note_box';
        widgetContent = {
          content: item.description || item.title || '',
        };
      } else if (bType === 'feature') {
        widgetType = 'feature_grid';
        widgetContent = {
          title: item.title || 'Features',
          features: parseJsonSafe(item.description, DEFAULT_PARTNER_FEATURES),
        };
      } else if (bType === 'step') {
        widgetType = 'step_process';
        widgetContent = {
          title: item.title || 'Step Process',
          steps: parseJsonSafe(item.description, DEFAULT_PARTNER_STEPS),
        };
      } else if (bType === 'banner') {
        widgetType = 'media_banner';
        widgetContent = {
          title: item.title || '',
          image: item.image || '',
          alt: item.alt || '',
          link: item.href || '',
        };
      } else if (bType === 'video') {
        widgetType = 'video_player';
        widgetContent = {
          title: item.title || '',
          url: item.href || '',
          poster: item.image || '',
        };
      } else if (bType === 'tab') {
        widgetType = 'grade_tabs';
        widgetContent = {
          title: item.title || '',
          tabs: parseJsonSafe(item.description, DEFAULT_GRADE_TABS),
        };
      } else if (bType === 'reach_us') {
        widgetType = 'reach_us_boxes';
        widgetContent = {
          title: item.title || 'Reach Us',
          boxes: parseJsonSafe(item.description, DEFAULT_REACH_US_BOXES),
        };
      } else if (bType === 'faq') {
        widgetType = 'faq_accordion';
        widgetContent = {
          title: item.title || 'FAQ',
          items: parseJsonSafe(item.description, []),
        };
      } else {
        widgetType = 'card';
        widgetContent = {
          eyebrow: item.eyebrow || '',
          title: item.title || '',
          description: item.description || '',
          image: item.image || '',
          alt: item.alt || '',
          href: item.href || '',
          cta_label: item.cta_label || '',
          status_text: item.status_text || '',
        };
      }

      const widget = createDefaultWidget(widgetType, {
        name: item.title || `Block ${idx + 1}`,
        content: widgetContent,
      });
      cardsContainer.children.push(widget);
    });

    cardsSection.children = [cardsContainer];
    sections.push(cardsSection);
  }

  // Fallback: at least one section
  if (sections.length === 0) {
    sections.push(createDefaultSection('Section 1'));
  }

  return {
    version: 1,
    id: pageRecord.id || generateId('page'),
    title: pageRecord.title || 'Untitled Page',
    slug: pageRecord.slug || '',
    route: pageRecord.route || (pageRecord.slug ? `/content/${pageRecord.slug}` : ''),
    layout,
    status: pageRecord.status || 'draft',
    navbar_label: pageRecord.navbar_label || '',
    is_active: pageRecord.is_active !== false,
    display_order: Number(pageRecord.display_order || 0),
    seo: {
      title: pageRecord.seo_title || pageRecord.seo?.title || '',
      description: pageRecord.seo_description || pageRecord.seo?.description || '',
      keywords: pageRecord.seo_keywords || pageRecord.seo?.keywords || '',
      og_image: pageRecord.og_image || pageRecord.seo?.og_image || '',
      canonical_url: pageRecord.canonical_url || pageRecord.seo?.canonical_url || '',
    },
    header: pageRecord.header || JSON.parse(JSON.stringify(DEFAULT_HEADER_CONFIG)),
    footer: pageRecord.footer || JSON.parse(JSON.stringify(DEFAULT_FOOTER_CONFIG)),
    sections,
    customFields,
  };
}

/**
 * Converts a canonical document back into legacy page record shape,
 * preserving backwards compatibility with existing API endpoints, MongoDB collections,
 * and storefront page templates without losing any custom fields.
 */
export function canonicalToLegacy(canonicalDoc = {}) {
  // Retain all existing custom fields from original document
  const baseFields = { ...(canonicalDoc.customFields || {}) };

  let eyebrow = baseFields.eyebrow || '';
  let excerpt = baseFields.excerpt || '';
  let featured_image = baseFields.featured_image || baseFields.banner_image || '';
  let banner_image = baseFields.banner_image || baseFields.featured_image || '';
  let cta_text = baseFields.cta_text || '';
  let cta_url = baseFields.cta_url || '';
  const textParts = [];
  const page_items = [];

  let features_json = baseFields.features_json;
  let partner_steps = baseFields.partner_steps;
  let video_url = baseFields.video_url;
  let whatsapp_url = baseFields.whatsapp_url;
  let phone = baseFields.phone;
  let email = baseFields.email;
  let reach_us = baseFields.reach_us;
  let grade_tabs = baseFields.grade_tabs;
  let faqs = baseFields.faqs;

  // Traverse canonical sections to extract both legacy fields and individual blocks
  if (Array.isArray(canonicalDoc.sections)) {
    canonicalDoc.sections.forEach((sec) => {
      if (Array.isArray(sec.children)) {
        sec.children.forEach((con) => {
          if (Array.isArray(con.children)) {
            con.children.forEach((wgt) => {
              if (wgt.type === 'page_hero') {
                if (wgt.content?.eyebrow) eyebrow = wgt.content.eyebrow;
                if (wgt.content?.excerpt) excerpt = wgt.content.excerpt;
                if (wgt.content?.featured_image || wgt.content?.image) {
                  featured_image = wgt.content.featured_image || wgt.content.image;
                  banner_image = featured_image;
                }
                if (wgt.content?.cta_text) cta_text = wgt.content.cta_text;
                if (wgt.content?.cta_url) cta_url = wgt.content.cta_url;
              } else if (wgt.type === 'heading') {
                if (wgt.content?.text) {
                  const lvl = wgt.content?.level || 'h2';
                  const pfx = lvl === 'h1' ? '#' : lvl === 'h3' ? '###' : lvl === 'h4' ? '####' : '##';
                  textParts.push(`${pfx} ${wgt.content.text}`);
                }
              } else if (wgt.type === 'text') {
                if (wgt.content?.content) textParts.push(wgt.content.content);
              } else if (wgt.type === 'feature_grid') {
                if (wgt.content?.features) {
                  features_json = JSON.stringify(wgt.content.features);
                }
                page_items.push({
                  block_type: 'feature',
                  title: wgt.content?.title || 'Why Choose Us',
                  description: typeof wgt.content?.features === 'string' ? wgt.content.features : JSON.stringify(wgt.content?.features || []),
                  display_order: page_items.length,
                });
              } else if (wgt.type === 'step_process') {
                if (wgt.content?.steps) {
                  partner_steps = JSON.stringify(wgt.content.steps);
                }
                page_items.push({
                  block_type: 'step',
                  title: wgt.content?.title || 'How to Partner',
                  description: typeof wgt.content?.steps === 'string' ? wgt.content.steps : JSON.stringify(wgt.content?.steps || []),
                  display_order: page_items.length,
                });
              } else if (wgt.type === 'media_banner') {
                page_items.push({
                  block_type: 'banner',
                  title: wgt.content?.title || 'Media Banner',
                  image: wgt.content?.image || wgt.content?.url || '',
                  alt: wgt.content?.alt || '',
                  href: wgt.content?.link || '',
                  display_order: page_items.length,
                });
              } else if (wgt.type === 'video_player') {
                if (wgt.content?.url) video_url = wgt.content.url;
                page_items.push({
                  block_type: 'video',
                  title: wgt.content?.title || 'Inspection Video',
                  href: wgt.content?.url || '',
                  image: wgt.content?.poster || '',
                  display_order: page_items.length,
                });
              } else if (wgt.type === 'contact_channels') {
                if (wgt.content?.whatsapp) whatsapp_url = wgt.content.whatsapp;
                if (wgt.content?.phone) phone = wgt.content.phone;
                if (wgt.content?.email) email = wgt.content.email;
                page_items.push({
                  block_type: 'reach_us',
                  title: 'Contact Channels',
                  description: JSON.stringify(wgt.content || {}),
                  display_order: page_items.length,
                });
              } else if (wgt.type === 'reach_us_boxes') {
                if (wgt.content?.boxes) {
                  reach_us = JSON.stringify(wgt.content.boxes);
                }
                page_items.push({
                  block_type: 'reach_us',
                  title: wgt.content?.title || 'Reach Us',
                  description: typeof wgt.content?.boxes === 'string' ? wgt.content.boxes : JSON.stringify(wgt.content?.boxes || []),
                  display_order: page_items.length,
                });
              } else if (wgt.type === 'grade_tabs') {
                if (wgt.content?.tabs) {
                  grade_tabs = JSON.stringify(wgt.content.tabs);
                }
                page_items.push({
                  block_type: 'tab',
                  title: wgt.content?.title || 'Grading Tabs',
                  description: typeof wgt.content?.tabs === 'string' ? wgt.content.tabs : JSON.stringify(wgt.content?.tabs || []),
                  display_order: page_items.length,
                });
              } else if (wgt.type === 'faq_accordion') {
                if (wgt.content?.items) {
                  faqs = JSON.stringify(wgt.content.items);
                }
                page_items.push({
                  block_type: 'faq',
                  title: wgt.content?.title || 'FAQ Accordion',
                  description: typeof wgt.content?.items === 'string' ? wgt.content.items : JSON.stringify(wgt.content?.items || []),
                  display_order: page_items.length,
                });
              } else if (wgt.type === 'image' || wgt.type === 'image_block') {
                page_items.push({
                  block_type: 'image',
                  title: wgt.content?.caption || wgt.name || '',
                  image: wgt.content?.url || wgt.content?.image || '',
                  alt: wgt.content?.alt || '',
                  href: wgt.content?.link || '',
                  display_order: page_items.length,
                });
              } else if (wgt.type === 'button') {
                page_items.push({
                  block_type: 'action',
                  title: wgt.content?.label || 'Click Here',
                  href: wgt.content?.url || '',
                  cta_label: wgt.content?.label || 'Learn more',
                  display_order: page_items.length,
                });
              } else if (['card', 'action_button', 'note_box', 'hero_banner', 'promo_banner'].includes(wgt.type)) {
                const block_type = wgt.type === 'action_button' ? 'action' : wgt.type === 'note_box' ? 'note' : 'card';
                page_items.push({
                  block_type,
                  eyebrow: wgt.content?.eyebrow || wgt.content?.badge || '',
                  title: wgt.content?.title || wgt.content?.heading || wgt.name || '',
                  description: wgt.content?.description || wgt.content?.subheading || '',
                  image: wgt.content?.image || '',
                  alt: wgt.content?.alt || '',
                  href: wgt.content?.href || wgt.content?.button_url || '',
                  cta_label: wgt.content?.cta_label || wgt.content?.button_text || '',
                  status_text: wgt.content?.status_text || '',
                  display_order: page_items.length,
                });
              }
            });
          }
        });
      }
    });
  }

  // Preserve existing page_items if the builder didn't produce any new ones (safeguard)
  const finalPageItems = page_items.length > 0 ? page_items : (baseFields.page_items || []);

  const richContent = textParts.length > 0 ? textParts.join('\n\n') : (baseFields.content || '');

  return {
    ...baseFields,
    id: canonicalDoc.id,
    title: canonicalDoc.title || baseFields.title || 'Untitled Page',
    slug: canonicalDoc.slug || baseFields.slug || '',
    route: canonicalDoc.route || baseFields.route || (canonicalDoc.slug ? `/content/${canonicalDoc.slug}` : ''),
    layout: canonicalDoc.layout || baseFields.layout || 'custom',
    status: canonicalDoc.status || baseFields.status || 'draft',
    navbar_label: canonicalDoc.navbar_label ?? baseFields.navbar_label ?? '',
    is_active: canonicalDoc.is_active !== false,
    display_order: Number(canonicalDoc.display_order ?? baseFields.display_order ?? 0),
    eyebrow,
    excerpt,
    featured_image,
    banner_image,
    cta_text,
    cta_url,
    content: richContent,
    policy_content: richContent || baseFields.policy_content || '',
    page_items: finalPageItems,
    sections: canonicalDoc.sections || [],
    features_json,
    partner_steps,
    video_url,
    whatsapp_url,
    phone,
    email,
    reach_us,
    grade_tabs,
    faqs,
    seo_title: canonicalDoc.seo?.title || baseFields.seo_title || '',
    seo_description: canonicalDoc.seo?.description || baseFields.seo_description || '',
    seo_keywords: canonicalDoc.seo?.keywords || baseFields.seo_keywords || '',
    og_image: canonicalDoc.seo?.og_image || baseFields.og_image || '',
    canonical_url: canonicalDoc.seo?.canonical_url || baseFields.canonical_url || '',
    header: canonicalDoc.header || null,
    footer: canonicalDoc.footer || null,
  };
}
